import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Download, 
  X, 
  FileText,
  Calendar,
  List
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { startBackgroundExport } from '@/utils/organization/backgroundExport';
import { ReportsExportList } from '@/components/organisation/ReportsExportList';
import { cleanFileLabel, MAX_FILE_LABEL_LENGTH } from '@/shared/reportFileName';
import { getReportsExportDownloadFilename } from '@/shared/reportsTabLabel';
import { effectiveAsOf } from '@/shared/ptDates';
import { getReportsExportSourceName, getReportsExportAvailableDataSources } from '@/shared/reportsExportSources';

/**
 * UC-121 AC-10: the variant/SKU-grain Reports tab's export dialog title reads
 * "Export Variants" (not "Export Products") -- UI copy only, matching
 * OrganisationReportsTab.tsx's own tab label. Exported so tests can call the
 * real title-generation logic instead of a hand-copied replica.
 */
export function getReportsExportManagerTitle(activeReportTab: string): string {
  return `Export ${activeReportTab === 'skus' ? 'Variants' :
    activeReportTab === 'fixed-costs' ? 'Fixed Costs' : 'Sales History'}`;
}

interface ReportsExportManagerProps {
  organisation: any;
  isOpen: boolean;
  onClose: () => void;
  activeReportTab: string;
  filters: any;
  activeFilters?: Array<{ column: string; value: string; operation: string }>;
}

export const ReportsExportManager: React.FC<ReportsExportManagerProps> = ({
  organisation,
  isOpen,
  onClose,
  activeReportTab,
  filters,
  activeFilters = [],
}) => {
  const [view, setView] = useState<'new' | 'list'>('new');
  const [exportSettings, setExportSettings] = useState({
    dataSource: '',
    format: 'csv',
    includeHeaders: true,
  });
  // Optional label appended to the default file name (FIG-333). Raw text as typed; cleaned for display and on save.
  const [fileLabel, setFileLabel] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const exportInFlight = useRef(false);
  const { toast } = useToast();
  
  // Completion and failure notices for report exports are owned by OrganisationReportsTab, so they keep
  // arriving after this panel is closed (it closes on every tab change, FIG-239).

  // The file name never depends on the Source or any other export setting: the default name
  // (date the data runs to, merchant, report) plus the user's optional label. It is built by the
  // same function the List view uses, so the name shown here is the name that list offers later.
  const exportFileName = getReportsExportDownloadFilename(
    organisation?.name ?? '',
    activeReportTab,
    null,
    filters?.asOfDate,
    fileLabel,
  );

  // Sync export source with the report's active dataSources filter
  React.useEffect(() => {
    const availableSources = getAvailableDataSources();
    if (availableSources.length === 0 || availableSources[0] === 'No supported connections') return;

    const activeDataSources = filters.dataSources || [];
    
    if (activeDataSources.length === 0) {
      // No filter active — pick first available
      const defaultSource = availableSources.find(s => s !== 'All') || availableSources[0];
      setExportSettings(prev => ({ ...prev, dataSource: defaultSource }));
      return;
    }

    // Check if all non-"All" sources are selected → use "All"
    const nonAllSources = availableSources.filter(s => s !== 'All');
    const allSelected = nonAllSources.length > 1 && activeDataSources.length >= nonAllSources.length;
    
    if (allSelected && availableSources.includes('All')) {
      setExportSettings(prev => ({ ...prev, dataSource: 'All' }));
      return;
    }

    // Single source selected — map connection ID back to display name
    if (activeDataSources.length === 1) {
      const connId = activeDataSources[0];
      const matchedName = mapConnectionIdToExportName(connId);
      if (matchedName && availableSources.includes(matchedName)) {
        setExportSettings(prev => ({ ...prev, dataSource: matchedName }));
        return;
      }
    }

    // Multiple (but not all) sources — default to first matched or "All" if available
    if (availableSources.includes('All')) {
      setExportSettings(prev => ({ ...prev, dataSource: 'All' }));
    } else {
      const defaultSource = availableSources.find(s => s !== 'All') || availableSources[0];
      setExportSettings(prev => ({ ...prev, dataSource: defaultSource }));
    }
  }, [organisation?.connections, filters.dataSources]);

  function mapConnectionIdToExportName(connectionId: string): string | null {
    if (!organisation?.connections) return null;
    const connections = Object.values(organisation.connections) as any[];
    const conn = connections.find((c: any) => (c.id || c.connectionId) === connectionId);
    if (!conn) return null;
    return getReportsExportSourceName(conn);
  }

  const getAvailableDataSources = () => getReportsExportAvailableDataSources(organisation?.connections);

  const handleExport = async () => {
    // A second click before the button has re-rendered as disabled must not start a second export.
    if (exportInFlight.current) return;
    if (!organisation?.connections || organisation.connections.length < 1) {
      toast({
        title: 'No Connections',
        description: 'No data connections available for export',
        variant: 'destructive'
      });
      return;
    }

    exportInFlight.current = true;
    setIsExporting(true);
    
    try {
      // Prepare filters, removing dataSources and pagination from table filters
      const { dataSources: _tableDataSources, page: _page, pageSize: _pageSize, ...restFilters } = filters;
      // Map display names to backend filter values
      const dataSourceFilterMap: Record<string, string> = {
        'Amazon Selling Partner': 'amazon_selling_partner',
        'Amazon CSV Import': 'amazon_csv',
        'Shopify': 'Shopify',
        'Shopify CSV Import': 'shopify_csv',
      };
      const mappedSource = dataSourceFilterMap[exportSettings.dataSource] || exportSettings.dataSource;
      const exportFilters = {
        ...(exportSettings.dataSource === 'All' ? restFilters : { ...restFilters, dataSource: mappedSource }),
        columnFilters: activeFilters,
        // Freeze the day a trailing-12-month export runs as of. Left empty it would be resolved when the worker
        // runs, so an export started just before PT midnight and run (or retried) after it would hold the next
        // day's window under the earlier day's file name, which is built from the export's creation time.
        ...(restFilters.trailing12Months ? { asOfDate: effectiveAsOf(restFilters.asOfDate) } : {}),
      };

      // The label is stored with the export, so the List view names the file the same way later.
      const cleanedLabel = cleanFileLabel(fileLabel);

      // Use background export for consistency and proper cost allocation
      const result = await startBackgroundExport({
        connectionId: `report-${activeReportTab}`,
        schema: 'reports',
        tableName: activeReportTab,
        filters: [],
        customSQL: JSON.stringify({
          reportType: activeReportTab,
          organisationId: organisation.id,
          filters: exportFilters,
          // Top-level (never inside filters, which the report worker spreads into its request).
          ...(cleanedLabel ? { fileLabel: cleanedLabel } : {}),
        })
      });

      if (result.success) {
        toast({
          title: 'Background Export Started',
          description: `Export started. You'll be notified when ready.`
        });
        
        setFileLabel('');
        // Switch to list view to show the export progress
        setView('list');
      } else {
        throw new Error(result.error || 'Failed to start background export');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Failed to export report',
        variant: 'destructive'
      });
    } finally {
      exportInFlight.current = false;
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Card className="fixed bottom-4 right-4 w-96 shadow-lg z-50" data-testid="reports-export-manager">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {view === 'new' ? getReportsExportManagerTitle(activeReportTab) : 'Recent Exports'}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant={view === 'new' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setView('new')}
              className="h-7 px-2"
              data-testid="toggle-new-export"
            >
              <Download className="h-3 w-3 mr-1" />
              New
            </Button>
            <Button
              variant={view === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setView('list')}
              className="h-7 px-2"
              data-testid="toggle-recent-exports"
            >
              <List className="h-3 w-3 mr-1" />
              List
            </Button>
            <Button onClick={onClose} variant="ghost" size="sm" data-testid="close-export-manager">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      {view === 'new' ? (
        <CardContent className="pt-0 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="data-source" className="text-sm font-medium">Source</Label>
            <Select 
              value={exportSettings.dataSource} 
              onValueChange={(value) => setExportSettings({ ...exportSettings, dataSource: value })}
            >
              <SelectTrigger id="data-source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getAvailableDataSources().map((source) => (
                  <SelectItem key={source} value={source}>{source}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="format" className="text-sm font-medium">Format</Label>
            <Select 
              value={exportSettings.format} 
              onValueChange={(value) => setExportSettings({ ...exportSettings, format: value })}
            >
              <SelectTrigger id="format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label id="export-file-name-label" className="text-sm font-medium">Filename</Label>
            <p
              aria-labelledby="export-file-name-label"
              className="text-sm break-all rounded-md border bg-muted px-3 py-2"
              data-testid="export-file-name"
            >
              {exportFileName}
            </p>
            <Label htmlFor="export-file-label" className="text-xs text-muted-foreground">
              Add optional label to be appended to the filename
            </Label>
            <Input
              id="export-file-label"
              value={fileLabel}
              onChange={(e) => setFileLabel(e.target.value)}
              placeholder="Append label"
              maxLength={MAX_FILE_LABEL_LENGTH}
              autoComplete="off"
              aria-describedby="export-file-label-help"
              data-testid="export-file-label"
            />
            <p id="export-file-label-help" className="text-xs text-muted-foreground">
              Up to {MAX_FILE_LABEL_LENGTH} characters. Letters, numbers and - are kept; other characters become _.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="include-headers"
              checked={exportSettings.includeHeaders}
              onChange={(e) => setExportSettings({ ...exportSettings, includeHeaders: e.target.checked })}
              className="rounded border-gray-300"
            />
            <Label htmlFor="include-headers" className="text-sm">Include column headers</Label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button 
              onClick={handleExport} 
              disabled={isExporting}
              className="flex-1"
            >
              <Download className={`h-4 w-4 mr-2 ${isExporting ? 'animate-bounce' : ''}`} />
              {isExporting ? 'Exporting...' : 'Export Report'}
            </Button>
            <Button 
              onClick={onClose} 
              variant="outline"
            >
              Cancel
            </Button>
          </div>

          {filters.trailing12Months && (
            <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
              <Calendar className="h-3 w-3 inline mr-1" />
              Export will include trailing 12 months filter
            </div>
          )}
        </CardContent>
      ) : (
        <CardContent className="p-0">
          <ReportsExportList
            isOpen={true}
            onClose={() => setView('new')}
            organisationId={organisation.id}
            organisationName={organisation.name}
          />
        </CardContent>
      )}
    </Card>
  );
};

export default ReportsExportManager;